/* eslint-disable @typescript-eslint/no-explicit-any */
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ApiErrorResponse, ApiOkResponse, ApiResponse } from 'apisauce';
import { RcInstanceDB } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import queryClient from '~/queryClient';
import { UploadBundleProgressEvent } from '~/services/bundleUploadService';
import { activityTemplateService } from '../services/activityTemplateAPI';
import { OkAction, ErrorAction } from './baseActions';
import { bundleUploadService } from '../services';
import { RobocloudError } from '../types/common';
import { ActivityTemplateAction } from './activityTemplateActions';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

type AppState = import('~/store').AppState;

export enum PackageActionType {
  UPLOAD_PACKAGE = 'UPLOAD_PACKAGE',
  UPLOAD_PACKAGE_OK = 'UPLOAD_PACKAGE_OK',
  UPLOAD_PACKAGE_ERROR = 'UPLOAD_PACKAGE_ERROR',
  UPDATE_PACKAGE_UPLOAD_PROGRESS = 'UPDATE_PACKAGE_UPLOAD_PROGRESS',
  GET_PACKAGE_UPLOAD_INFO = 'GET_PACKAGE_UPLOAD_INFO',
  GET_PACKAGE_UPLOAD_INFO_OK = 'GET_PACKAGE_UPLOAD_INFO_OK',
  GET_PACKAGE_UPLOAD_INFO_ERROR = 'GET_PACKAGE_UPLOAD_INFO_ERROR',
  GET_PACKAGE_DOWNLOAD_LINK = 'GET_PACKAGE_DOWNLOAD_LINK',
  GET_PACKAGE_DOWNLOAD_LINK_OK = 'GET_PACKAGE_DOWNLOAD_LINK_OK',
  GET_PACKAGE_DOWNLOAD_LINK_ERROR = 'GET_PACKAGE_DOWNLOAD_LINK_ERROR',
  PACKAGE_PROCESSING_START = 'PACKAGE_PROCESSING_START',
  PACKAGE_PROCESSING_PEEK = 'PACKAGE_PROCESSING_PEEK',
  PACKAGE_PROCESSING_COMPLETED = 'PACKAGE_PROCESSING_COMPLETED',
  PACKAGE_PROCESSING_ERROR = 'PACKAGE_PROCESSING_ERROR',
  PACKAGE_BUILDING_PROGRESS = 'PACKAGE_BUILDING_PROGRESS',
  HANDLE_GIT_ROBOT = 'HANDLE_GIT_ROBOT',
  HANDLE_GIT_ROBOT_OK = 'HANDLE_GIT_ROBOT_OK',
  HANDLE_GIT_ROBOT_ERROR = 'HANDLE_GIT_ROBOT_ERROR',
}

type WithId = { id: string };

type UploadPackageAction =
  | (Action<PackageActionType.UPLOAD_PACKAGE> & WithId)
  | (Action<PackageActionType.UPLOAD_PACKAGE_OK> & WithId)
  | (ErrorAction<PackageActionType.UPLOAD_PACKAGE_ERROR> & WithId)
  | (Action<PackageActionType.UPDATE_PACKAGE_UPLOAD_PROGRESS> &
      WithId & { percentCompleted: number });

type GetPackageUploadInfoAction =
  | (Action<PackageActionType.GET_PACKAGE_UPLOAD_INFO> & WithId)
  | (Action<PackageActionType.GET_PACKAGE_UPLOAD_INFO_OK> & WithId)
  | (ErrorAction<PackageActionType.GET_PACKAGE_UPLOAD_INFO_ERROR> & WithId);

type GetPackageDownloadLinkAction =
  | (Action<PackageActionType.GET_PACKAGE_DOWNLOAD_LINK> & WithId)
  | (OkAction<PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_OK, string | undefined> & WithId)
  | (ErrorAction<PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_ERROR> & WithId);

type PollingStateAction =
  | (Action<PackageActionType.PACKAGE_PROCESSING_START> & WithId)
  | (OkAction<PackageActionType.PACKAGE_BUILDING_PROGRESS, RcInstanceDB.ActivityTemplate> & WithId)
  | (OkAction<PackageActionType.PACKAGE_PROCESSING_COMPLETED, RcInstanceDB.ActivityTemplate> &
      WithId)
  | (Action<PackageActionType.PACKAGE_PROCESSING_PEEK> & WithId)
  | (Action<PackageActionType.PACKAGE_PROCESSING_ERROR> & WithId);

type HandleGitRobotAction =
  | (Action<PackageActionType.HANDLE_GIT_ROBOT> & WithId)
  | (OkAction<PackageActionType.HANDLE_GIT_ROBOT_OK, RcInstanceDTO.ActivityTemplate> & WithId)
  | (ErrorAction<PackageActionType.HANDLE_GIT_ROBOT_ERROR> & WithId);

export type PackageAction =
  | UploadPackageAction
  | GetPackageUploadInfoAction
  | GetPackageDownloadLinkAction
  | PollingStateAction
  | HandleGitRobotAction;

type ProgressEvent = { loaded: number; total: number };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPackageUploadInfo =
  (
    templateId: string,
  ): ThunkAction<Promise<ApiResponse<any>>, AppState, null, GetPackageUploadInfoAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: PackageActionType.GET_PACKAGE_UPLOAD_INFO,
      id: templateId,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.getPackageUploadInfo({
      workspaceId,
      id: templateId,
    });
    if (response.ok) {
      dispatch({
        type: PackageActionType.GET_PACKAGE_UPLOAD_INFO_OK,
        id: templateId,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: PackageActionType.GET_PACKAGE_UPLOAD_INFO_ERROR,
        id: templateId,
      });
    }

    return response;
  };

const sendPackage =
  (
    url: string,
    fields: any,
    file: File,
    templateId: string,
  ): ThunkAction<Promise<ApiResponse<any>>, AppState, null, UploadPackageAction> =>
  async (dispatch) => {
    const form = new FormData();

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(fields)) {
      form.append(key, value as string | Blob);
    }

    form.append('file', file);

    const config = {
      onUploadProgress: (progressEvent: UploadBundleProgressEvent) => {
        const percentCompleted = progressEvent.total
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 100;
        dispatch({
          type: PackageActionType.UPDATE_PACKAGE_UPLOAD_PROGRESS,
          percentCompleted,
          id: templateId,
        });
      },
    };

    dispatch({
      type: PackageActionType.UPLOAD_PACKAGE,
      id: templateId,
    });

    const response = await bundleUploadService.uploadBundle(url, form, config);
    if (response.ok) {
      dispatch({
        type: PackageActionType.UPLOAD_PACKAGE_OK,
        id: templateId,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: PackageActionType.UPLOAD_PACKAGE_ERROR,
        id: templateId,
      });
    }

    return response;
  };

// TODO replace this using websockets

// we implement a polling mechanism to see when files are fully processed
// take into account that the "right" way to do that is with websockets
// but for simplicity for now we have setup this
const PROCESSING_STATE_POLLING_RATE = 500;
const MAX_PROCESSING_STATE_DELAY = 3600 * 1000; /* 1 hour */

export const peekProcessingState =
  (templateId: string, delay = 0): ThunkAction<void, AppState, null, PackageAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: PackageActionType.PACKAGE_PROCESSING_PEEK,
      id: templateId,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.getActivityTemplateById({
      workspaceId,
      id: templateId,
    });

    if (response.ok && response.data !== undefined) {
      const activityTemplate = response.data;
      queryClient.setQueryData(['activity_templates', activityTemplate.id], activityTemplate);
      if (
        (activityTemplate.config as any).state !== 'WAITING' &&
        (activityTemplate.config as any).state !== 'IMAGE_BUILDING'
      ) {
        dispatch({
          type: PackageActionType.PACKAGE_PROCESSING_COMPLETED,
          id: templateId,
          data: activityTemplate,
        });
        queryClient.invalidateQueries([getState().ui.currentWorkspaceId, 'activity_templates']);
      } else if (delay < MAX_PROCESSING_STATE_DELAY) {
        if ((activityTemplate.config as any).state === 'IMAGE_BUILDING') {
          dispatch({
            type: PackageActionType.PACKAGE_BUILDING_PROGRESS,
            id: templateId,
            data: activityTemplate,
          });
        }

        await new Promise<void>((resolve) => {
          const timeoutId = window.setTimeout(() => {
            clearTimeout(timeoutId);
            dispatch(peekProcessingState(templateId, delay + PROCESSING_STATE_POLLING_RATE));
            resolve();
          }, PROCESSING_STATE_POLLING_RATE);
        });
      } else {
        dispatch({
          type: PackageActionType.PACKAGE_PROCESSING_ERROR,
          id: templateId,
          data: activityTemplate,
        });
      }
    } else {
      dispatch({
        type: PackageActionType.PACKAGE_PROCESSING_ERROR,
        id: templateId,
      });
    }
  };

export const startPollingProcess =
  (templateId: string): ThunkAction<void, AppState, null, PackageAction> =>
  async (dispatch) => {
    dispatch({
      type: PackageActionType.PACKAGE_PROCESSING_START,
      id: templateId,
    });

    dispatch(peekProcessingState(templateId));
  };

export const uploadPackage =
  (templateId: string, file: File): ThunkAction<void, AppState, null, ActivityTemplateAction> =>
  async (dispatch) => {
    const postInfoResponse = await dispatch(getPackageUploadInfo(templateId));

    if (postInfoResponse.ok) {
      const { fields } = postInfoResponse.data.postInfo;
      const { url } = postInfoResponse.data.postInfo;
      const upload = await dispatch(sendPackage(url, fields, file, templateId));

      if (upload.ok) {
        // file uploading is async, the activity template is not immediately updated
        dispatch(startPollingProcess(templateId));
      }
    }
  };

export const getPackageDownloadLink =
  (
    templateId: string,
  ): ThunkAction<Promise<string | undefined>, AppState, null, GetPackageDownloadLinkAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: PackageActionType.GET_PACKAGE_DOWNLOAD_LINK,
      id: templateId,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.getPackageDownloadLink({
      workspaceId,
      id: templateId,
    });
    if (response.ok) {
      dispatch({
        type: PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_OK,
        data: response.data,
        id: templateId,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_ERROR,
        id: templateId,
      });
    }

    return response.ok ? response.data : undefined;
  };

export const handleGitRobot =
  (
    id: string,
    url: string,
  ): ThunkAction<
    Promise<ApiOkResponse<RcInstanceDTO.ActivityTemplate> | ApiErrorResponse<RobocloudError>>,
    AppState,
    null,
    HandleGitRobotAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: PackageActionType.HANDLE_GIT_ROBOT, id });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.handleGitRobot({ workspaceId, id, url });
    if (response.ok) {
      dispatch({
        type: PackageActionType.HANDLE_GIT_ROBOT_OK,
        data: response.data,
        id,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: PackageActionType.HANDLE_GIT_ROBOT_ERROR,
        id,
      });
    }

    if (response.ok) {
      dispatch(startPollingProcess(id));
    }

    return response;
  };
